import { analytics } from './analytics.js';
import { configUrl, isStage, label, origin } from './snippet.js';
import { buildPageKey, externalReferrerHost } from './page.js';
import { maxRetryPolls, readyTimeoutMs, retryPollIntervalMs } from './constants.js';
import { runtime, widgetFrameUrl } from './runtime.js';

/**
 * The chat frame's whole life: mounting it on the first open, the ready handshake, the panel state it
 * has to be told about, and the recovery when it never answers. Second of init()'s surfaces to become
 * a factory.
 *
 * This is the one that writes rather than reads: ten fields of the shared state are set in here — the
 * frame element itself, whether its document is live, and the handovers waiting to be replayed once it
 * reports ready. They are fields rather than closure variables for exactly this reason. A plain `let`
 * captured at construction would leave every other surface looking at the frame that existed when the
 * instance came up, which after the first recovery remount is a detached element.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} The handles the rest of the instance needs
 */
export function createFrameLifecycle(ctx) {
    const { state, panel, hostOverrideApplies } = ctx;

    /* Server-reachability recovery: when the iframe never signals ready (server down during a
     * build/deploy → the browser's "connection refused" page renders inside the frame, where no
     * script of ours runs), the loader polls the public config endpoint from the embedding page
     * and remounts the iframe once the server answers again. */
    state.iframe = null;
    let readySinceLastLoad = false;

    /* Whether the frame currently holds the widget document — it announced itself and no later load
     * replaced it with a browser error page. Until then the frame still holds the initial about:blank
     * it was created with, and that document inherits the EMBEDDING page's origin: a message addressed
     * to the widget's origin is dropped there, and the browser writes the mismatch to the console of
     * somebody else's website. (Same-origin embeds — our own pages before the LeadWin agent got its own
     * hosts — matched by accident and never showed it.) Waiting costs nothing: handleFrameReady posts
     * the panel state itself, and the host theme rides along in the frame URL of the mount. */
    state.frameDocumentLive = false;
    let polling = false;
    let watchdogTimer = null;
    let retryTimer = null;
    let retryPollsLeft = maxRetryPolls;

    /* Id of the bubble the visitor clicked, carried into the frame URL of the open it triggered. Only an
     * id: the page resolves it back into the configured text, so nothing editable in the browser reaches
     * the chat as a message. Cleared once the frame reported ready — a recovery remount after a failed
     * first open must still ask the question the visitor clicked. */
    state.pendingNudgeId = null;

    /* Id of the page question the visitor clicked, carried into the frame URL like a nudge's. Set ONLY
     * by that click: the chat greets with a page question because the visitor tapped one, never because
     * the page they are on happens to have one. Someone who clicks the launcher asked for the widget,
     * not for the question — they get the Welcome Screen and no message at all. */
    state.pendingPageMessageId = null;

    /* Set when the visitor accepted a page question by clicking the answer offered next to it, instead
     * of just opening the bubble. Only a flag — the answer itself is re-resolved server-side from the
     * page message id, exactly like every other text here. Cleared with pendingNudgeId, for the same
     * reason: a recovery remount must still carry the answer the visitor gave. */
    state.pendingPageReply = false;

    /* Id of the campaign whose CTA opened the chat, carried into the frame URL like a clicked bubble's.
     * Only the id travels: the chat resolves the prewritten question (and the attribution) server-side,
     * so nothing editable in the browser becomes the campaign's message. Cleared with its siblings. */
    state.pendingAnnouncementId = null;

    /* A bubble the visitor clicked while the frame was already standing but its circuit was still coming
     * up, as {nudge, page, reply}. The frame URL was built before the click and cannot carry it, and a
     * message the circuit is not yet listening for is lost; so it waits here and is replayed on the
     * subscription handshake, like the article and the question below. Only ids and a flag. */
    state.pendingBubble = null;

    /* Whether the frame this document mounts still stands for a new visit. Set from the start and
     * cleared the moment a document reported ready: the widget opens on the Welcome Screen once per
     * page load, not once per mount. A recovery remount after a failed first open still counts as that
     * one open, and the language command's remount mid-conversation must not send the visitor back to
     * the greeting — the server resumes their conversation there, which is the whole point of it. */
    let freshDocument = true;

    /* The review or project whose card the visitor tapped, held until the frame's circuit takes
     * messages and replayed on its subscription handshake. Only a message type and a key: the widget
     * page loads the entry itself and shows nothing when it belongs to another provider. */
    state.pendingEvidence = null;

    /* Screen the JS API asked for while nobody could take it yet (frame unmounted, or mounted with its
     * circuit still starting): carried into the next frame URL and replayed once the circuit announces
     * itself. Cleared on that replay — a request must open its panel once, not on every reconnect. */
    state.pendingScreen = null;

    /* Help-Center article the JS API asked for while nobody could take it yet. Held like the evidence
     * above and replayed on the subscription handshake rather than carried into the frame URL: a URL
     * keeps its deep link for the life of the frame, so every later reload of it — a dropped circuit
     * reconnecting — would reopen an article the visitor had just closed. Only an id travels; the page
     * loads the article itself and refuses one outside this widget's own knowledge base. */
    state.pendingArticleId = null;

    /* Question the JS API asked in the visitor's name while nobody could take it yet, as the validated
     * {prompt, newChat} pair — the command's two halves travel together, because a question replayed
     * without its newChat would land in the wrong conversation. Held and replayed exactly like the article
     * above, and kept out of the frame URL for the same reason: a reload of the frame would ask the
     * question a second time, in the middle of the answer to the first. */
    state.pendingPrompt = null;

    /* The look the host page's own switch asked for, for this visitor on this page. It outranks the
     * widget's configured theme; null means "no override": the widget resolves its look itself. */
    state.hostTheme = null;

    /* Whether the CURRENT frame's circuit has announced it takes API messages (showpanel/theme).
     * Before that, requests go the pending route above; reset on every (re)mount. */
    state.frameSubscribed = false;

    function clearRecoveryTimers() {
        if (watchdogTimer !== null) {
            clearTimeout(watchdogTimer);
            watchdogTimer = null;
        }
        if (retryTimer !== null) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    }

    /**
     * Crossfades between the loading dots and the frame's document. Visible dots keep the first open
     * honest (something IS coming) instead of presenting a blank box; once the document paints, the
     * frame fades in over them. The dots node lives in the panel from creation (see createPanel).
     * @param {boolean} loading - True while nothing inside the frame is worth looking at yet
     */
    function setFrameLoading(loading) {
        const dots = panel.querySelector('#leadwin-agent-loading');
        if (dots) {
            dots.style.display = loading ? 'flex' : 'none';
        }
        if (state.iframe) {
            state.iframe.style.opacity = loading ? '0' : '1';
        }
    }

    /** The widget page announced itself — the server is reachable, recovery stands down. */
    function handleFrameReady() {
        readySinceLastLoad = true;
        setFrameLoading(false); // A page that speaks has painted — the dots hand over.
        state.frameDocumentLive = true; // The document that answers to `origin` is on screen from here on.
        polling = false;
        retryPollsLeft = maxRetryPolls; // A later outage in the same session gets a fresh budget.
        state.pendingNudgeId = null;          // The page has the question; a later remount must not repeat it.
        state.pendingPageMessageId = null;
        state.pendingPageReply = false;
        state.pendingAnnouncementId = null;
        freshDocument = false;                // This page load has had its Welcome Screen.
        clearRecoveryTimers();

        // The page mounts assuming it is on screen (it is only ever mounted on an open). Say so
        // explicitly anyway, in case the panel was collapsed again before the circuit came up.
        postPanelState(state.panelOpen === true);
    }

    /**
     * The widget page said it is about to reload its own document — its circuit could not be resumed
     * (every deploy does that) or a saved interface language needs a fresh page. Put the dots back over
     * the frame now, before the old document fades to white and the new one paints in pieces, so the
     * reload looks like the open it is. The frame element stays: the page navigates itself, and the
     * ready handshake of the new document reveals it exactly like a first open.
     * The watchdog is armed for the same reason it is on a mount: should the navigation land on a
     * browser error page (the server went down again in between), the recovery takes over from there.
     */
    function handleFrameReloading() {
        state.frameDocumentLive = false; // The document that answered is on its way out.
        state.frameSubscribed = false;   // Its circuit with it; the next one announces itself anew.
        setFrameLoading(true);
        armReadyWatchdog();
    }

    /**
     * Tells the widget page whether it is actually in front of the visitor. The panel keeps its iframe
     * (and with it the Blazor circuit) after a close, so without this signal the page cannot tell a chat
     * somebody is reading from one they collapsed — which is exactly what the read marker turns on.
     * @param {boolean} open - Whether the panel is expanded right now
     */
    function postPanelState(open) {
        if (!state.iframe || !state.iframe.contentWindow || !state.frameDocumentLive) {
            return; // Nothing there to hear it — handleFrameReady posts the state that counts.
        }

        try {
            state.iframe.contentWindow.postMessage({ type: 'feedbax:widget:panel', open: open }, origin);
        } catch {
            // Frame torn down between the check and the call — it comes up open, and the next toggle
            // corrects any drift.
        }
    }

    /** Starts polling unless the page's ready signal arrives within the timeout. */
    function armReadyWatchdog() {
        clearRecoveryTimers();
        watchdogTimer = setTimeout(function () {
            watchdogTimer = null;
            polling = true;
            pollServer();
        }, readyTimeoutMs);
    }

    /** Probes the config endpoint; remounts the iframe on success, keeps polling otherwise. */
    function pollServer() {
        if (!polling || typeof fetch !== 'function') {
            return;
        }
        if (retryPollsLeft <= 0) {
            polling = false;
            setFrameLoading(false); // Nothing more is coming — dots that keep pulsing would be a lie.
            console.warn('[leadwin-agent] server unreachable, giving up on retries.');
            return;
        }

        retryPollsLeft -= 1;
        fetch(configUrl, { cache: 'no-store' })
            .then(function (response) {
                if (!polling) {
                    return; // Ready arrived while the probe was in flight.
                }
                if (response.ok) {
                    polling = false;
                    mountFrame(); // Fresh navigation replaces the browser error page.
                } else if (response.status === 429) {
                    // Throttled, not down. Probing on would spend the whole retry budget on requests
                    // the server is already refusing and hold the window open by refilling it — the one
                    // behaviour a rate limit must not provoke. The next page load is the retry.
                    polling = false;
                    setFrameLoading(false);
                    console.warn('[leadwin-agent] rate limited, stopping availability probes.');
                } else {
                    scheduleNextPoll(); // Up but not healthy yet (e.g. proxy 502 mid-deploy).
                }
            })
            .catch(function () {
                if (polling) {
                    scheduleNextPoll(); // Still down (mid-build) — keep trying.
                }
            });
    }

    function scheduleNextPoll() {
        if (retryTimer === null) {
            retryTimer = setTimeout(function () {
                retryTimer = null;
                pollServer();
            }, retryPollIntervalMs);
        }
    }

    /**
     * An in-frame navigation finished. `load` fires after the page's own ready signal, so no
     * ready since the previous load means the frame landed on a browser error page (e.g. the
     * circuit's auto-reload raced a second server restart) — start recovery for it.
     */
    function handleFrameLoad() {
        // Whatever landed, it painted — reveal it. The widget page was usually revealed by its ready
        // signal already; an error page is what the recovery below is FOR, and hiding it behind
        // dots that never stop would dress a dead server up as a loading one.
        setFrameLoading(false);

        if (!readySinceLastLoad) {
            state.frameDocumentLive = false; // An error page answers to nothing this file addresses.
            armReadyWatchdog();
        }
        readySinceLastLoad = false;
    }

    /**
     * The frame URL for this mount, with the analytics session and whatever the visitor clicked to get
     * here appended. A page question rides along only when they clicked its bubble — every other open
     * (the launcher, the JS API, a tapped quote card) mounts on the Welcome Screen with no message,
     * because none of them asked to be asked something. Only ids travel; the server resolves them back
     * into text.
     */
    function buildFrameUrl() {
        const base = widgetFrameUrl();
        let url = base + analytics.frameQuery(base);

        // Where the visitor is, and where they came from. Consent-free and identifier-free: both are
        // facts about THIS page load, which the frame request carries anyway — the journey across pages
        // is what needs the stored session id, and that one stays behind the consent gate.
        // The page KEY, not the bare path: on a site whose pages live in a query parameter the path alone
        // names a dozen of them at once, and the assistant would answer about the wrong one. The server
        // re-applies the provider's parameter list to what arrives here, so nothing is trusted about it.
        url += (url.indexOf('?') >= 0 ? '&' : '?')
            + 'page=' + encodeURIComponent(buildPageKey(runtime.pageQueryKeys));

        const referrerHost = externalReferrerHost();
        if (referrerHost) {
            url += '&ref=' + encodeURIComponent(referrerHost);
        }

        if (state.pendingNudgeId) {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + 'nudge=' + encodeURIComponent(state.pendingNudgeId);
        }

        if (state.pendingAnnouncementId) {
            url += (url.indexOf('?') >= 0 ? '&' : '?')
                + 'announcement=' + encodeURIComponent(state.pendingAnnouncementId);
        }

        if (state.pendingPageMessageId) {
            url += (url.indexOf('?') >= 0 ? '&' : '?')
                + 'pagemsg=' + encodeURIComponent(state.pendingPageMessageId);

            // The visitor accepted the question rather than merely opening it: the chat starts with the
            // question AND their answer, so the assistant is already replying when the panel finishes
            // opening. Only meaningful next to a clicked question — the answer belongs to that one.
            if (state.pendingPageReply) {
                url += '&pagereply=1';
            }
        }

        // The JS API's deep link: the page mounts straight on the requested panel, and in the look the
        // host page asked for where it asked for one. Names only, validated both here (apiScreens) and
        // server-side; without the parameter the widget's own setting decides.
        if (state.pendingScreen) {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + 'panel=' + encodeURIComponent(state.pendingScreen);
        }
        // Only where the override is allowed to speak (see effectiveTheme): handing a forced widget
        // down the parameter would have the page open in a look the corner around it does not wear.
        if (hostOverrideApplies()) {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + 'theme=' + state.hostTheme;
        }

        // A new visit opens on the Welcome Screen: the page drops the conversation binding it restored,
        // so the launcher shows the greeting and the starters instead of a transcript from before the
        // reload. The conversation stays under the messages tab. See freshDocument above for why this is
        // per page load and not per mount.
        if (freshDocument) {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + 'fresh=1';
        }

        // The simulated visit's marker, as on the config request: the page is framed from the stage, so
        // without it the stage's host would count as the provider's website (see isStage).
        if (isStage) {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + 'stage=1';
        }

        return url;
    }

    /** Creates (or replaces) the chat iframe and arms the ready watchdog for its load. */
    function mountFrame() {
        const frame = document.createElement('iframe');
        frame.src = buildFrameUrl();
        frame.title = label;
        frame.setAttribute('allow', 'clipboard-write');
        // Transparent, not #fff: the PANEL already paints the theme-aware ground (panelChrome), and a
        // white frame behind it flashed the dark look white for the whole first load. Mounted invisible
        // (opacity 0 over the loading dots) and faded in by setFrameLoading once its document painted.
        frame.style.cssText = 'width:100%;height:100%;border:none;display:block;background:transparent;'
            + 'opacity:0;transition:opacity 250ms ease;';
        frame.addEventListener('load', handleFrameLoad);
        readySinceLastLoad = false;
        state.frameDocumentLive = false; // Until it answers, the frame is the about:blank it was created as.
        state.frameSubscribed = false;   // The fresh document's circuit has to announce itself anew.
        if (state.iframe) {
            state.iframe.remove();
        }
        state.iframe = frame;
        panel.appendChild(frame);
        setFrameLoading(true); // Every mount starts over the dots — recovery and language remounts too.
        armReadyWatchdog();
    }

    return { handleFrameReady, handleFrameReloading, postPanelState, mountFrame };
}
